import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Modal, message } from "antd";
import { PlusOutlined, LeftOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import {
  deletePodcast,
  getPodcast,
  getPodcastAvailableSources,
  listPodcasts,
  regeneratePodcast,
  usePlayer,
} from "@vojomo-core/shared";
import type { Podcast, PodcastAvailableSources, PodcastDataSource } from "@vojomo-core/shared";
import { PodcastCard } from "./components/PodcastCard";
import { CreatePodcastDrawer } from "./components/CreatePodcastDrawer";

// ── Player modal ───────────────────────────────────────────────────────────────

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

const PodcastPlayerModal = ({
  podcast,
  onClose,
}: {
  podcast: Podcast | null;
  onClose: () => void;
}) => {
  const { controls, state } = usePlayer();
  const hasStarted = useRef(false);

  useEffect(() => {
    if (podcast?.audio_url && !hasStarted.current) {
      hasStarted.current = true;
      controls.play({ itemId: podcast.audio_url, itemType: "local" });
    }
    return () => {
      controls.stop();
      hasStarted.current = false;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [podcast?._id]);

  if (!podcast) return null;

  return (
    <Modal
      open={!!podcast}
      onCancel={() => {
        controls.stop();
        onClose();
      }}
      footer={null}
      title={null}
      width={480}
      centered
    >
      <div className="flex flex-col items-center gap-4 py-4">
        <div className="text-4xl">🎙</div>
        <div className="text-center">
          <h2 className="text-lg font-semibold text-gray-900 mb-1">
            {podcast.title}
          </h2>
          <p className="text-sm text-gray-400">
            {podcast.podcast_type.replace("_", " ")} · {podcast.podcast_length}
          </p>
        </div>

        <div className="flex items-center gap-4">
          <Button
            type="primary"
            shape="circle"
            size="large"
            icon={
              state.paused ? (
                <span style={{ fontSize: 18 }}>▶</span>
              ) : (
                <span style={{ fontSize: 18 }}>⏸</span>
              )
            }
            onClick={() =>
              controls.togglePlay({ itemId: podcast.audio_url!, itemType: "local" })
            }
          />
        </div>

        {state.duration > 0 && (
          <div className="w-full">
            <input
              type="range"
              min={0}
              max={state.duration}
              value={state.time}
              onChange={(e) => controls.seek(Number(e.target.value))}
              className="w-full accent-blue-600"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>{formatTime(state.time)}</span>
              <span>{formatTime(state.duration)}</span>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

// ── QBR starter templates ──────────────────────────────────────────────────────

type DrawerDefaults = {
  podcastType?: Podcast["podcast_type"];
  podcastLength?: Podcast["podcast_length"];
  podcastTone?: Podcast["podcast_tone"];
  sources?: PodcastDataSource[];
};

const QBR_TEMPLATES: {
  key: string;
  icon: string;
  label: string;
  description: string;
  defaults: Omit<DrawerDefaults, "sources">;
  staticSources?: PodcastDataSource[];
  useLatestQbr?: boolean;
}[] = [
  {
    key: "team_scoring",
    icon: "📊",
    label: "Team Scoring Analysis",
    description: "Deep dive into your team's QBR scores and progress notes",
    defaults: { podcastType: "analysis", podcastLength: "standard", podcastTone: "analytical" },
    useLatestQbr: true,
  },
  {
    key: "qbr_objectives",
    icon: "🎯",
    label: "Next Quarter Objectives",
    description: "Briefing on objectives and commitments for the upcoming quarter",
    defaults: { podcastType: "briefing", podcastLength: "brief", podcastTone: "collaborative" },
    staticSources: [{ type: "objectives", building_block: "quarterlyObjectives" }],
  },
  {
    key: "roadmap",
    icon: "🗺️",
    label: "Company Roadmap",
    description: "Narrative walkthrough of your company's vision, strategy, and goals",
    defaults: { podcastType: "deep_dive", podcastLength: "standard", podcastTone: "inspirational" },
    staticSources: [{ type: "roadmap_building_blocks" }],
  },
  {
    key: "annual_objectives",
    icon: "📅",
    label: "Annual Objectives Review",
    description: "Review progress toward this year's strategic objectives",
    defaults: { podcastType: "analysis", podcastLength: "standard", podcastTone: "analytical" },
    staticSources: [{ type: "objectives", building_block: "oneYearObjectives" }],
  },
];

// ── Main Podcasts page ─────────────────────────────────────────────────────────

export const Podcasts = () => {
  const navigate = useNavigate();
  const [podcasts, setPodcasts] = useState<Podcast[]>([]);
  const [availableSources, setAvailableSources] = useState<PodcastAvailableSources | null>(null);
  const [loading, setLoading] = useState(true);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerDefaults, setDrawerDefaults] = useState<DrawerDefaults | undefined>(undefined);
  const [playingPodcast, setPlayingPodcast] = useState<Podcast | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchPodcasts = useCallback(async () => {
    try {
      const data = await listPodcasts();
      setPodcasts(data);
    } catch {
      // silently fail on background polls
    }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([
        fetchPodcasts(),
        getPodcastAvailableSources().then(setAvailableSources).catch(() => null),
      ]);
      setLoading(false);
    };
    init();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll every 5s while any podcast is generating
  useEffect(() => {
    const hasGenerating = podcasts.some(
      (p) => p.status === "generating" || p.status === "pending"
    );
    if (hasGenerating && !pollRef.current) {
      pollRef.current = setInterval(fetchPodcasts, 5000);
    } else if (!hasGenerating && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [podcasts, fetchPodcasts]);

  const handleCreated = (podcast: Podcast) => {
    setPodcasts((prev) => [podcast, ...prev]);
  };

  const handlePlay = async (podcast: Podcast) => {
    try {
      const fresh = await getPodcast(podcast._id);
      setPlayingPodcast(fresh);
    } catch {
      setPlayingPodcast(podcast);
    }
  };

  const handleRegenerate = async (podcast: Podcast) => {
    try {
      await regeneratePodcast(podcast._id);
      setPodcasts((prev) =>
        prev.map((p) =>
          p._id === podcast._id ? { ...p, status: "generating" as Podcast["status"] } : p
        )
      );
      message.info("Regenerating podcast…");
    } catch {
      message.error("Failed to regenerate podcast");
    }
  };

  const handleDelete = async (podcastId: string) => {
    try {
      await deletePodcast(podcastId);
      setPodcasts((prev) => prev.filter((p) => p._id !== podcastId));
      message.success("Podcast deleted");
    } catch {
      message.error("Failed to delete podcast");
    }
  };

  const handleUpdate = (updated: Partial<Podcast> & { _id: string }) => {
    setPodcasts((prev) =>
      prev.map((p) => (p._id === updated._id ? { ...p, ...updated } : p))
    );
  };

  const openWithTemplate = (tpl: (typeof QBR_TEMPLATES)[0]) => {
    let sources: PodcastDataSource[] = tpl.staticSources ?? [];
    if (tpl.useLatestQbr && availableSources?.qbr_periods?.[0]) {
      const latest = availableSources.qbr_periods[0];
      sources = [{ type: "qbr_scoring", session_id: latest.session_id, period_key: latest.period_key }];
    }
    setDrawerDefaults({ ...tpl.defaults, sources });
    setDrawerOpen(true);
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      {/* Breadcrumb */}
      <button
        onClick={() => navigate("/studio/audio/home")}
        className="flex items-center gap-1 text-sm text-gray-400 hover:text-blue-600 mb-6 transition-colors"
      >
        <LeftOutlined style={{ fontSize: 11 }} />
        Audio
      </button>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xl">🎙</span>
            <h1 className="text-2xl font-bold text-gray-900">Podcasts</h1>
          </div>
          <p className="text-sm text-gray-400">
            Generate podcast episodes from your team's data
          </p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => { setDrawerDefaults(undefined); setDrawerOpen(true); }}
          size="large"
        >
          Create Podcast
        </Button>
      </div>

      {/* QBR starter templates — always visible */}
      <div className="mb-8">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-3">
          Start from a template
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {QBR_TEMPLATES.map((tpl) => (
            <button
              key={tpl.key}
              onClick={() => openWithTemplate(tpl)}
              className="text-left rounded-xl border border-gray-200 bg-white p-4 flex flex-col gap-2 hover:border-blue-400 hover:shadow-sm transition-all"
            >
              <span className="text-2xl">{tpl.icon}</span>
              <div className="text-xs font-semibold text-gray-800 leading-snug">
                {tpl.label}
              </div>
              <div className="text-xs text-gray-400 leading-snug">{tpl.description}</div>
              <span className="text-xs text-blue-500 font-medium mt-auto">
                Start →
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Podcast library */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-40 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : podcasts.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="text-4xl mb-3">🎙</div>
          <h2 className="text-base font-semibold text-gray-700 mb-1">No podcasts yet</h2>
          <p className="text-sm text-gray-400 max-w-xs">
            Choose a template above or create a custom podcast from any of your team's data.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {podcasts.map((podcast) => (
            <PodcastCard
              key={podcast._id}
              podcast={podcast}
              onPlay={handlePlay}
              onRegenerate={handleRegenerate}
              onDelete={handleDelete}
              onUpdate={handleUpdate}
            />
          ))}
        </div>
      )}

      {/* Create drawer */}
      <CreatePodcastDrawer
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setDrawerDefaults(undefined); }}
        onCreated={handleCreated}
        defaults={drawerDefaults}
      />

      {/* Player modal */}
      <PodcastPlayerModal
        podcast={playingPodcast}
        onClose={() => setPlayingPodcast(null)}
      />
    </div>
  );
};
